import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Close as CloseIcon,
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon,
  AttachMoney as AttachMoneyIcon,
  Receipt as ReceiptIcon,
  CalendarToday as CalendarIcon,
  Notes as NotesIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { api } from '../../utils/apiService';

const PaymentDialog = ({ open, onClose, invoice, onSubmit }) => {
  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentMethod: '',
    paymentDate: format(new Date(), 'yyyy-MM-dd'),
    reference: '',
    notes: ''
  });

  const [error, setError] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingMethods, setLoadingMethods] = useState(false);

  // Fetch payment methods from API
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      setLoadingMethods(true);
      try {
        const response = await api.get('/masters/payment-methods');

        if (response.data && Array.isArray(response.data)) {
          setPaymentMethods(response.data);
          // Set first method as default if available
          if (response.data.length > 0 && !paymentData.paymentMethod) {
            setPaymentData(prev => ({
              ...prev,
              paymentMethod: response.data[0]._id
            }));
          }
        } else {
          // Fallback to default methods
          const defaultMethods = [
            { _id: 'cash', name: 'Cash', category: 'cash' },
            { _id: 'card', name: 'Credit/Debit Card', category: 'card' },
            { _id: 'bank_transfer', name: 'Bank Transfer', category: 'bank_transfer' },
            { _id: 'cheque', name: 'Cheque', category: 'cheque' },
            { _id: 'online', name: 'Online Payment', category: 'online' },
            { _id: 'other', name: 'Other', category: 'other' }
          ];
          setPaymentMethods(defaultMethods);
          setPaymentData(prev => ({
            ...prev,
            paymentMethod: 'cash'
          }));
        }
      } catch (error) {
        console.error('Error fetching payment methods:', error);
        // Fallback to default methods on error
        const defaultMethods = [
          { _id: 'cash', name: 'Cash', category: 'cash' },
          { _id: 'card', name: 'Credit/Debit Card', category: 'card' },
          { _id: 'bank_transfer', name: 'Bank Transfer', category: 'bank_transfer' },
          { _id: 'cheque', name: 'Cheque', category: 'cheque' },
          { _id: 'online', name: 'Online Payment', category: 'online' },
          { _id: 'other', name: 'Other', category: 'other' }
        ];
        setPaymentMethods(defaultMethods);
        setPaymentData(prev => ({
          ...prev,
          paymentMethod: 'cash'
        }));
      } finally {
        setLoadingMethods(false);
      }
    };

    if (open) {
      fetchPaymentMethods();
    }
  }, [open]);

  useEffect(() => {
    if (invoice && open) {
      setPaymentData(prev => ({
        ...prev,
        amount: invoice.balanceAmount || 0
      }));
      setError('');
    }
  }, [invoice, open]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setPaymentData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = () => {
    // Validation
    if (!paymentData.amount || paymentData.amount <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    if (parseFloat(paymentData.amount) > parseFloat(invoice.balanceAmount)) {
      setError('Payment amount cannot exceed balance amount');
      return;
    }

    if (!paymentData.paymentMethod) {
      setError('Please select a payment method');
      return;
    }

    onSubmit({
      ...paymentData,
      invoiceId: invoice._id || invoice.id,
      invoiceNo: invoice.id || invoice.invoiceNo
    });
  };

  const handleClose = () => {
    setPaymentData({
      amount: '',
      paymentMethod: paymentMethods.length > 0 ? paymentMethods[0]._id : '',
      paymentDate: format(new Date(), 'yyyy-MM-dd'),
      reference: '',
      notes: ''
    });
    setError('');
    onClose();
  };

  if (!invoice) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon color="primary" />
          <Box>
            <Typography variant="h6" fontWeight="600">
              Record Payment
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {invoice.id || invoice.invoiceNo} • {invoice.customer || invoice.customerName}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Quick Summary */}
        <Box sx={{
          mb: 3,
          p: 2,
          bgcolor: 'grey.50',
          borderRadius: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total Amount
            </Typography>
            <Typography variant="h6" fontWeight="600">
              SAR {Number(invoice.grandTotal).toFixed(2)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              Balance Due
            </Typography>
            <Typography variant="h6" fontWeight="700" color="error.main">
              SAR {Number(invoice.balanceAmount || 0).toFixed(2)}
            </Typography>
          </Box>
        </Box>

        {/* Payment Form */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Payment Amount"
              type="number"
              value={paymentData.amount}
              onChange={handleChange('amount')}
              InputProps={{
                startAdornment: <InputAdornment position="start">SAR</InputAdornment>,
              }}
              inputProps={{
                min: 0,
                max: invoice.balanceAmount,
                step: 0.01
              }}
              helperText={`Maximum: SAR ${Number(invoice.balanceAmount || 0).toFixed(2)}`}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth disabled={loadingMethods}>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={paymentData.paymentMethod}
                onChange={handleChange('paymentMethod')}
                label="Payment Method"
              >
                {loadingMethods ? (
                  <MenuItem value="">Loading...</MenuItem>
                ) : paymentMethods.length === 0 ? (
                  <MenuItem value="">No payment methods available</MenuItem>
                ) : (
                  paymentMethods.map((method) => (
                    <MenuItem key={method._id} value={method._id}>
                      {method.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Payment Date"
              type="date"
              value={paymentData.paymentDate}
              onChange={handleChange('paymentDate')}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Reference"
              value={paymentData.reference}
              onChange={handleChange('reference')}
              placeholder="Optional"
              inputProps={{
                maxLength: 20
              }}
              helperText={`${paymentData.reference.length}/20`}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              value={paymentData.notes}
              onChange={handleChange('notes')}
              multiline
              rows={2}
              placeholder="Optional"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={<PaymentIcon />}
        >
          Record Payment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDialog;
